import EntityModel from '@/Modules/Entity/EntityModel.js'
import UserModel from '@/Modules/User/UserModel.js'
import _ from 'lodash'
import { i18n } from '@/Setup/SetupI18n.js'
import QuoteModel from '@/Modules/Quote/QuoteModel.js'
import InvoiceModel from '@/Modules/Invoice/InvoiceModel.js'
import BinderModel from '@/Modules/Binder/BinderModel.js'
import ClientModel from '@/Modules/Client/ClientModel.js'
import StatementModel from '@/Modules/Statement/StatementModel.js'
import EmailTemplateModel from '@/Modules/EmailTemplate/EmailTemplateModel.js'
import SchemaModel from '@/Modules/Schema/SchemaModel.js'
import MessageModel from '@/Modules/Message/MessageModel.js'

export const ModelTypeClassMap = Object.freeze({
  quote: QuoteModel,
  invoice: InvoiceModel,
  user: UserModel,
  entity: EntityModel,
  binder: BinderModel,
  client: ClientModel,
  statement: StatementModel,
  email_template: EmailTemplateModel,
  schema: SchemaModel,
  message: MessageModel,
})

export default class ActivityModel {
  id = null
  type = 'note'
  activityDate = null
  subject = null
  state = null
  remaining = null
  userId = null
  priority = 2
  description = null
  canClose = null
  canReactivate = null
  entityId = null
  reminderDate = null
  notes = []
  modelId = null
  modelType = null

  user = null
  entity = null
  model = null

  constructor (data = {}) {
    _.forEach(data, (value, field) => {
      this[_.camelCase(field)] = value
    })

    this.user = data.user && new UserModel(this.user)
    this.entity = data.entity && new EntityModel(this.entity)
    this.model = data.model
  }

  isActive () {
    return this.state === 'active'
  }

  isCompleted () {
    return this.state === 'completed'
  }

  isCanceled () {
    return this.state === 'canceled'
  }

  set model(data) {
    if (!this.modelType || !this.modelId) {
      return null
    }

    if (_.has(ModelTypeClassMap, this.modelType)) {
      return new (_.get(ModelTypeClassMap, this.modelType))(data || {})
    }

    console.log(`${ this.modelType } is not defined in ModelTypeClassMap`)
    return null
  }

  get modelDataProperties() {
    if (!this.model) {
      return null
    }

    switch (this.modelType) {
      case 'quote':
        return {
          label: this.model.data.number,
          tooltip: `${ i18n.tc('common.quote') } - ${ this.model.data.number }`,
          route: { name: 'quotes.show', params: { quoteId: this.model.data.id } },
        }
      case 'invoice':
        return {
          label: this.model.number,
          tooltip: `${ i18n.tc('common.invoice') } - ${ this.model.number }`,
          route: { name: 'invoices.show', params: { invoiceId: this.model.id } },
        }
      case 'entity':
        return {
          label: this.model.name,
          tooltip: `${ i18n.tc('common.entity') } - ${ this.model.name }`,
          route: { name: 'entities.show', params: { entityId: this.model.id } },
        }
      default:
        return {
          label: '',
          tooltip: null,
          route: {},
        }
    }
  }

  static priorities () {
    return [
      {
        label: i18n.tc('priorities.high'),
        value: 1,
      },
      {
        label: i18n.tc('priorities.normal'),
        value: 2,
      },
      {
        label: i18n.tc('priorities.low'),
        value: 3,
      },
    ]
  }

  priorityData () {
    switch (this.priority) {
      case 1:
        return {
          label: i18n.tc('priorities.high'),
          badge: 'soft-info',
        }
      case 2:
        return {
          label: i18n.tc('priorities.normal'),
          badge: 'soft-success',
        }
      case 3:
        return {
          label: i18n.tc('priorities.low'),
          badge: 'soft-danger',
        }
    }
  }

  static types () {
    return [
      {
        label: i18n.tc('activities.type-task'),
        value: 'task',
      },
      {
        label: i18n.tc('activities.type-note'),
        value: 'note',
      },
      {
        label: i18n.tc('activities.type-email'),
        value: 'email',
      },
      {
        label: i18n.tc('activities.type-call'),
        value: 'call',
      },
      {
        label: i18n.tc('activities.type-meeting'),
        value: 'meeting',
      }
    ]
  }

  typeData () {
    switch (this.type) {
      case 'task':
        return {
          label: i18n.tc('activities.type-task'),
          icon: 'clipboard-notes',
        }
      case 'note':
        return {
          label: i18n.tc('activities.type-note'),
          icon: 'notes',
        }
      case 'email':
        return {
          label: i18n.tc('activities.type-email'),
          icon: 'at',
        }
      case 'call':
        return {
          label: i18n.tc('activities.type-call'),
          icon: 'calling',
        }
      case 'meeting':
        return {
          label: i18n.tc('activities.type-meeting'),
          icon: 'clipboard',
        }
    }
  }

  statusData () {
    switch (this.state) {
      case 'active':
        return {
          label: i18n.tc('statuses.active'),
          badge: 'soft-info',
        }
      case 'completed':
        return {
          label: i18n.tc('statuses.completed'),
          badge: 'soft-success',
        }
      case 'canceled':
        return {
          label: i18n.tc('statuses.canceled'),
          badge: 'soft-danger',
        }
    }
  }
}
